import { Select } from "antd";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowsSelectOption,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../../components/TextField";
import { getWorkflowOptionLabel } from "../../../../../../../utils/workflowOptionLabels";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import {
  SelectContainer,
  TextFieldCss,
  popupOptionsCss,
} from "../RuleComponent.styles";

interface ISleepFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  ruleConditionComparators?: IWorkflowsSelectOption[];
  ruleConditionTriggers?: IWorkflowsSelectOption[];
  sleepStages?: IWorkflowsSelectOption[];
}

export function SleepFields({
  formik,
  ruleConditionComparators,
  ruleConditionTriggers,
  sleepStages,
}: ISleepFieldsProps) {
  const { t } = useTranslation();

  const shouldShowConditionError = (field: keyof IRuleCondition): string => {
    const touchedArray = formik.touched.conditions as
      | FormikTouched<IRuleCondition>[]
      | undefined;
    const errorArray = formik.errors.conditions as
      | FormikErrors<IRuleCondition>[]
      | undefined;

    if (touchedArray && errorArray) {
      const touched = touchedArray[0];
      const error = errorArray[0];

      return touched?.[field] && error?.[field] ? (error[field] as string) : "";
    }

    return "";
  };

  const stageOptions: IOption[] = useMemo(
    () =>
      sleepStages?.map((stage) => ({
        label: getWorkflowOptionLabel("sleepStage", stage, t),
        value: stage.type,
        key: stage.type,
      })) ?? [],
    [sleepStages, t]
  );

  // Sleep rules only support the simple comparators — every baseline-based comparator
  // (% from baseline, less/greater than baseline) is excluded. The backend computes no baseline
  // for sleep and throws on these, so they must not be selectable.
  const comparatorOptions: IOption[] = useMemo(
    () =>
      ruleConditionComparators
        ?.filter((c) => !c.type.includes("Baseline"))
        .map((c) => ({
          label: getWorkflowOptionLabel("comparator", c, t),
          value: c.type,
          key: c.type,
        })) ?? [],
    [ruleConditionComparators, t]
  );

  const triggerOptions: IOption[] = useMemo(
    () =>
      ruleConditionTriggers
        ?.filter((trigger) => trigger.type !== "HighestInSession")
        .map((trigger) => ({
          label: getWorkflowOptionLabel("trigger", trigger, t),
          value: trigger.type,
          key: trigger.type,
        })) ?? [],
    [ruleConditionTriggers, t]
  );

  const thresholdMinutes = formik.values.conditions[0].threshold;
  const thresholdHoursValue =
    thresholdMinutes !== undefined && thresholdMinutes !== null
      ? thresholdMinutes / 60
      : "";

  const handleThresholdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = event.target.value;

    if (raw === "") {
      formik.setFieldValue("conditions[0].threshold", undefined);
      return;
    }

    const hours = parseFloat(raw);

    if (Number.isNaN(hours)) {
      formik.setFieldValue("conditions[0].threshold", undefined);
      return;
    }

    formik.setFieldValue("conditions[0].threshold", Math.round(hours * 60));
  };

  const selectedStage = formik.values.conditions[0].sleepStage;
  const selectedComparator =
    formik.values.conditions[0].conditionComparatorType;

  return (
    <>
      <SelectContainer isError={shouldShowConditionError("sleepStage") !== ""}>
        <p>{t("workflows_rule_sleepStage")}</p>
        <Select
          placeholder={t("workflows_rule_totalSleep")}
          value={selectedStage}
          suffixIcon={<ArrowDown />}
          options={stageOptions}
          popupClassName={popupOptionsCss()}
          notFoundContent={t("workflows_rule_noData")}
          className={`${selectCss()} nopan nodrag`}
          onChange={(value) => {
            formik.setFieldValue("conditions[0].sleepStage", value);
          }}
        />
        <ErrorMessage>{shouldShowConditionError("sleepStage")}</ErrorMessage>
      </SelectContainer>

      {selectedStage !== undefined ? (
        <SelectContainer
          isError={shouldShowConditionError("conditionComparatorType") !== ""}
        >
          <p>{t("workflows_rule_comparator")}</p>
          <SelectDropdown
            placeholder={t("workflows_rule_lessThan")}
            value={selectedComparator}
            options={comparatorOptions}
            onValueChange={(value) =>
              formik.setFieldValue(
                "conditions[0].conditionComparatorType",
                value as string
              )
            }
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>
            {shouldShowConditionError("conditionComparatorType")}
          </ErrorMessage>
        </SelectContainer>
      ) : null}

      {selectedComparator !== undefined ? (
        <>
          <SelectContainer
            isError={shouldShowConditionError("threshold") !== ""}
          >
            <p>{t("workflows_rule_thresholdHours")}</p>
            <TextField
              name="conditions[0].threshold"
              placeholder="0"
              value={thresholdHoursValue}
              label=""
              onChange={handleThresholdChange}
              backgroudColor="white"
              className={`${TextFieldCss()} nopan nodrag`}
              type="number"
              maxDecimalPlaces={2}
            />
            <ErrorMessage>{shouldShowConditionError("threshold")}</ErrorMessage>
          </SelectContainer>

          <SelectContainer
            isError={shouldShowConditionError("triggerType") !== ""}
          >
            <p>{t("workflows_rule_ruleTrigger")}</p>
            <SelectDropdown
              placeholder={t("workflows_rule_anyReading")}
              value={formik.values.conditions[0].triggerType}
              options={triggerOptions}
              onValueChange={(value) =>
                formik.setFieldValue(
                  "conditions[0].triggerType",
                  value as string
                )
              }
              className={`${selectCss()} nopan nodrag`}
            />
            <ErrorMessage>
              {shouldShowConditionError("triggerType")}
            </ErrorMessage>
          </SelectContainer>
        </>
      ) : null}
    </>
  );
}
